import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { FloatLabelModule } from 'primeng/floatlabel';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { catchError, finalize, of, tap } from 'rxjs';
import { AnalyticsService } from '../../core/analytics.service';
import { AuthApiService } from '../../core/auth-api.service';
import { BrandService } from '../../core/brand.service';
import { ApiErrorResponse } from '../../core/auth.models';
import { PortalPreferenceService } from '../../core/portal-preference.service';
import { RedirectService } from '../../core/redirect.service';

@Component({
  selector: 'app-login',
  imports: [
    ButtonModule,
    CardModule,
    FloatLabelModule,
    IconFieldModule,
    InputIconModule,
    InputTextModule,
    MessageModule,
    ProgressSpinnerModule,
    ReactiveFormsModule,
    RouterLink,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.scss',
})
export class LoginComponent {
  private readonly title = inject(Title);
  private readonly authApi = inject(AuthApiService);
  private readonly redirectService = inject(RedirectService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly portalPreference = inject(PortalPreferenceService);
  private readonly analytics = inject(AnalyticsService);
  private readonly brand = inject(BrandService);

  public form: FormGroup = this.initForm();
  public loginError = '';
  public isLoading = false;
  public isRedirecting = false;
  public redirectMessage = '';
  public showPassword = false;
  public readonly legalNoticeUrl = this.brand.brand.footer.legalNoticeUrl;
  public readonly privacyPolicyUrl = this.brand.brand.footer.privacyPolicyUrl;
  public readonly brandProfile = this.brand.brand;

  constructor() {
    this.title.setTitle(this.brand.buildPageTitle('Acceso de clientes'));
  }

  public togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  public submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    const { username, password } = this.form.value;
    this.isLoading = true;
    this.isRedirecting = false;
    this.redirectMessage = '';
    this.loginError = '';
    this.analytics.trackEvent('login_attempt');

    this.authApi
      .customerLogin({ username, password })
      .pipe(
        tap((response) => {
          this.portalPreference.saveLastPortal({
            portalCode: response.data.portalCode,
            portalLabel: response.data.portalLabel,
          });
          this.analytics.trackEvent('login_redirect', { portal_code: response.data.portalCode });
          this.isRedirecting = true;
          this.redirectMessage = 'Redirigiendo a tu portal...';
          this.cdr.detectChanges();
          this.redirectService.go(response.data.redirectUrl);
        }),
        catchError((err: { error?: ApiErrorResponse }) => {
          this.analytics.trackEvent('login_error');
          this.loginError =
            err?.error?.error?.message ??
            err?.error?.message ??
            'Usuario o contraseña incorrectos';
          return of(null);
        }),
        finalize(() => {
          if (!this.isRedirecting) {
            this.isLoading = false;
          }
          this.cdr.detectChanges();
        })
      )
      .subscribe();
  }

  private initForm(): FormGroup {
    return this.formBuilder.group({
      username: [null, Validators.required],
      password: [null, Validators.required],
    });
  }
}
